import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import {
  DefaultHttpClient,
  HttpClient,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  HttpTransportType,
  type ILogger,
} from '@microsoft/signalr';
import type { Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthTokenService } from '../../services/auth-token.service';
import { SessionExpiryNoticeService } from '../../services/session-expiry-notice.service';
import {
  LIVE_KEEP_ALIVE_MS,
  LIVE_SERVER_TIMEOUT_MS,
  LiveConnectionLogger,
  StatusTrackingHttpClient,
  decideRetry,
  statusFromError,
} from './live-connection-policy';
import { normalizeJobChatMessageDto, parseJobChatThreadReadEvent } from './job-chat-api-parse';
import type { JobChatMessageDto, JobChatThreadReadEventDto } from './live-chat.service';
import type { DepositApprovalDecidedDto } from '../job/deposit/deposit-approval-decided.model';
import type { AccountingNoteChangedDto } from '../accounting/move-to-accounting/accounting-note-changed.model';
import type { JobNotesActivityChangedDto } from '../accounting/move-to-accounting/job-notes-activity-changed.model';
import type { ChangeRequestChangedDto } from '../accounting/move-to-accounting/change-request.model';
import type { SendBackToServiceChangedDto } from '../accounting/move-to-accounting/send-back-to-service.model';
import type { JobRoutingChangedDto } from '../accounting/move-to-accounting/job-routing.model';
import type { StoreManagerVerificationChangedDto } from '../accounting/move-to-accounting/store-manager-verification.model';
import type { VendorPayableChangedDto } from '../accounting/move-to-accounting/vendor-payable.model';
import type { InvoiceCustomerCardChangedDto } from '../accounting/move-to-accounting/invoice-customer-card.model';

/** Live signal that an on-site approval moved (submitted, admin_edited, vendor_edited, approved, ...). */
export interface OnsiteApprovalChangedDto {
  jobKey: string;
  vendorKey: string;
  estimateKey: string | null;
  kind: string;
  accountManagerKey: string | null;
  /** Admin who made the change (null for vendor events, or older Job Ops). */
  updatedByKey: string | null;
  updatedByName: string | null;
}

/**
 * Live signal that a NORMAL (not on-site) estimate's negotiation moved — Job Ops sends this one for the
 * admin Estimates tab (`BroadcastVendorEstimateChangedAsync`), never `OnsiteApprovalChanged`.
 */
export interface VendorEstimateChangedDto {
  jobKey: string;
  vendorKey: string;
  estimateKey: string | null;
  kind: string;
}

/** Another admin opened (viewing) or closed an on-site approval in the pop-up. */
export interface OnsiteViewingDto {
  jobKey: string;
  vendorKey: string;
  adminKey: string | null;
  adminName: string;
  viewing: boolean;
}

function parseOnsiteViewing(raw: unknown): OnsiteViewingDto | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const str = (a: string, b: string): string => String(o[a] ?? o[b] ?? '').trim();
  const jobKey = str('jobKey', 'JobKey');
  const vendorKey = str('vendorKey', 'VendorKey');
  if (!jobKey || !vendorKey) return null;
  return {
    jobKey,
    vendorKey,
    adminKey: str('adminKey', 'AdminKey') || null,
    adminName: str('adminName', 'AdminName') || 'Another admin',
    viewing: (o['viewing'] ?? o['Viewing']) !== false,
  };
}

function parseVendorEstimateChanged(raw: unknown): VendorEstimateChangedDto | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const str = (a: string, b: string): string => String(o[a] ?? o[b] ?? '').trim();
  const jobKey = str('jobKey', 'JobKey');
  if (!jobKey) return null;
  return {
    jobKey,
    vendorKey: str('vendorKey', 'VendorKey'),
    estimateKey: str('estimateKey', 'EstimateKey') || null,
    kind: str('kind', 'Kind').toLowerCase(),
  };
}

function parseOnsiteApprovalChanged(raw: unknown): OnsiteApprovalChangedDto | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const str = (a: string, b: string): string => String(o[a] ?? o[b] ?? '').trim();
  const jobKey = str('jobKey', 'JobKey');
  const vendorKey = str('vendorKey', 'VendorKey');
  if (!jobKey || !vendorKey) return null;
  return {
    jobKey,
    vendorKey,
    estimateKey: str('estimateKey', 'EstimateKey') || null,
    kind: str('kind', 'Kind').toLowerCase(),
    accountManagerKey: str('accountManagerKey', 'AccountManagerKey') || null,
    updatedByKey: str('updatedByKey', 'UpdatedByKey') || null,
    updatedByName: str('updatedByName', 'UpdatedByName') || null,
  };
}

/**
 * Self-hosted SignalR on Job Ops (`/hubs/job-chat`). Staff clients join `JoinStaffInbox`
 * for live inbox/badge updates; vendor clients use per-thread groups on the same hub.
 */
/** Why no retry timer is pending while the inbox is wanted but not connected. */
type LivePause = 'hidden' | 'offline' | 'refused' | 'session-expired' | 'no-token';

@Injectable({ providedIn: 'root' })
export class JobChatSignalRService implements OnDestroy {
  private readonly auth = inject(AuthTokenService);
  private readonly ngZone = inject(NgZone);
  private readonly sessionNotice = inject(SessionExpiryNoticeService);

  private hub?: HubConnection;
  private inboxJoined = false;
  /**
   * Something on the page wants the staff inbox (the chat pop-up always does). While true, a dropped
   * connection is brought back (a deploy restart, a laptop asleep, Wi-Fi) with a capped back-off, but
   * never while the tab is hidden or offline, and never after the session expired (VE-SIGNALR):
   * the pop-up's orange button and auto-open for the account manager depend on it.
   */
  private wantInbox = false;
  private connecting?: Promise<void>;
  /** The ONE retry timer. Every path that schedules a retry goes through {@link scheduleRetry}. */
  private retryTimer?: ReturnType<typeof setTimeout>;
  /** Failed attempts since the last good connection; drives the back-off. */
  private failedAttempts = 0;
  /** Set when retrying is paused with no timer; cleared by the event that resumes it. */
  private paused: LivePause | null = null;
  /** Fires at the token's `exp`: live updates stop exactly then and the banner shows. */
  private expiryTimer?: ReturnType<typeof setTimeout>;
  /** HTTP status of the last failed request of the current attempt (negotiate), from the tracking client. */
  private lastHttpStatus: number | null = null;
  /** One "disconnected" console line per outage, one "reconnected" line when it ends. */
  private outageLogged = false;
  private everConnected = false;
  private lifecycleWired = false;
  private readonly onOnline = () => this.resume('online');
  private readonly onVisibility = () => {
    if (this.isTabVisible()) this.resume('visible');
  };
  private tokenSub?: Subscription;
  private readonly hubLogger = new LiveConnectionLogger();
  /** Runs after the connection comes back: events sent while it was down are lost, so listeners re-read. */
  private readonly onInboxReconnectedHandlers = new Set<() => void>();
  private onMessage?: (dto: JobChatMessageDto) => void;
  private onThreadRead?: (dto: JobChatThreadReadEventDto) => void;
  private onDepositApprovalDecided?: (dto: DepositApprovalDecidedDto) => void;
  private onAccountingNoteChanged?: (dto: AccountingNoteChangedDto) => void;
  private onJobNotesActivityChanged?: (dto: JobNotesActivityChangedDto) => void;
  private onChangeRequestChanged?: (dto: ChangeRequestChangedDto) => void;
  private onJobRoutingChanged?: (dto: JobRoutingChangedDto) => void;
  private onStoreManagerVerificationChanged?: (dto: StoreManagerVerificationChangedDto) => void;
  // Multi-subscriber (unlike the other single-slot listeners above): both the vendor-payable card
  // and the new accounting-finalize-panel (2026-09-16) need to react to the same broadcasts
  // independently, and a single-slot setter would have the second registration silently overwrite
  // the first. Registration order otherwise follows the same setListener(handler) call shape as
  // every other event here, so existing call sites needed no changes.
  private readonly onVendorPayableChangedHandlers = new Set<(dto: VendorPayableChangedDto) => void>();
  private readonly onInvoiceCustomerCardChangedHandlers = new Set<(dto: InvoiceCustomerCardChangedDto) => void>();
  /** Move to Accounting Tab 1/2/3 membership changed (the three Accounting*ListMonitor polls). */
  private readonly onAccountingListsChangedHandlers = new Set<() => void>();
  /** Vendor List membership/rows changed (RFI-356) — a vendor was created, activated/deactivated
   *  or DNU-flagged. No payload: each open Vendor List refetches its current page/filter. */
  private readonly onVendorListChangedHandlers = new Set<() => void>();
  /**
   * Extra JobChatMessage subscribers alongside the pop-up's own single-slot {@link onMessage}. The
   * Estimates tab's per-estimate chat panel needs the same broadcasts, and calling
   * ensureInboxConnected with its own handler would silently replace the pop-up's.
   */
  private readonly onJobChatMessageHandlers = new Set<(dto: JobChatMessageDto) => void>();
  private readonly onOnsiteApprovalChangedHandlers = new Set<(dto: OnsiteApprovalChangedDto) => void>();
  private readonly onVendorEstimateChangedHandlers = new Set<(dto: VendorEstimateChangedDto) => void>();
  private readonly onOnsiteViewingHandlers = new Set<(dto: OnsiteViewingDto) => void>();
  // Also multi-subscriber, same reasoning: the Move to Accounting page's own
  // SendBackToServiceNotificationService AND the Account Manager-facing "Sent Back to Service"
  // job-list page (SentBackToServiceComponent, 2026-09-16) both need independent live updates.
  private readonly onSendBackToServiceChangedHandlers = new Set<(dto: SendBackToServiceChangedDto) => void>();

  /** Registers a listener for deposit-approval-decided events on the existing staff inbox connection. */
  setDepositApprovalDecidedListener(handler: (dto: DepositApprovalDecidedDto) => void): void {
    this.onDepositApprovalDecided = handler;
  }

  /** Registers a listener for accounting-note-changed events on the existing staff inbox connection. */
  setAccountingNoteChangedListener(handler: (dto: AccountingNoteChangedDto) => void): void {
    this.onAccountingNoteChanged = handler;
  }

  /** Registers a listener for job-notes-activity-changed events (📋 Job Notes / Activity) on the
   *  existing staff inbox connection. */
  setJobNotesActivityChangedListener(handler: (dto: JobNotesActivityChangedDto) => void): void {
    this.onJobNotesActivityChanged = handler;
  }

  /** Registers a listener for change-request-changed events (🔁 Send Change Request to Account
   *  Manager) on the existing staff inbox connection. */
  setChangeRequestChangedListener(handler: (dto: ChangeRequestChangedDto) => void): void {
    this.onChangeRequestChanged = handler;
  }

  /** Registers a listener for send-back-to-service-changed events (↩ Send Back to Service) on the
   *  existing staff inbox connection. Multi-subscriber -- returns an unsubscribe function, same
   *  reasoning as setVendorPayableChangedListener above. */
  setSendBackToServiceChangedListener(handler: (dto: SendBackToServiceChangedDto) => void): () => void {
    this.onSendBackToServiceChangedHandlers.add(handler);
    return () => this.onSendBackToServiceChangedHandlers.delete(handler);
  }

  /** Registers a listener for job-routing-changed events (JobRoutingLog: "Send back to Service" /
   *  "Send back to Accounting") on the existing staff inbox connection. */
  setJobRoutingChangedListener(handler: (dto: JobRoutingChangedDto) => void): void {
    this.onJobRoutingChanged = handler;
  }

  /** Registers a listener for store-manager-verification-changed events (✉ Store Manager
   *  Verification — Email) on the existing staff inbox connection. */
  setStoreManagerVerificationChangedListener(handler: (dto: StoreManagerVerificationChangedDto) => void): void {
    this.onStoreManagerVerificationChanged = handler;
  }

  /** Registers a listener for vendor-payable-changed events ("Approve Vendor(s) Payables")
   *  on the existing staff inbox connection. Multi-subscriber -- returns an unsubscribe function
   *  since more than one component (vendor-payable card, accounting-finalize-panel) may be alive
   *  and registering at once, unlike every other single-slot listener here. */
  setVendorPayableChangedListener(handler: (dto: VendorPayableChangedDto) => void): () => void {
    this.onVendorPayableChangedHandlers.add(handler);
    return () => this.onVendorPayableChangedHandlers.delete(handler);
  }

  /** Registers a listener for invoice-customer-card-changed events (automatic invoice creation,
   *  2026-09-15). Multi-subscriber, same reasoning as setVendorPayableChangedListener above. */
  setInvoiceCustomerCardChangedListener(handler: (dto: InvoiceCustomerCardChangedDto) => void): () => void {
    this.onInvoiceCustomerCardChangedHandlers.add(handler);
    return () => this.onInvoiceCustomerCardChangedHandlers.delete(handler);
  }

  /** Fires when a job enters or leaves Move to Accounting Tab 1, 2 or 3 -- the backend list monitors
   *  (InvoiceCustomerListChanged / AccountingBillsListChanged / AccountingPayablesReceivablesListChanged,
   *  each polling its stored proc every ~20s). Multi-subscriber; returns an unsubscribe function. */
  setAccountingListsChangedListener(handler: () => void): () => void {
    this.onAccountingListsChangedHandlers.add(handler);
    return () => this.onAccountingListsChangedHandlers.delete(handler);
  }

  /** Registers a listener for VendorListChanged (RFI-356) on the existing staff inbox connection.
   *  Multi-subscriber; returns an unsubscribe function. */
  setVendorListChangedListener(handler: () => void): () => void {
    this.onVendorListChangedHandlers.add(handler);
    return () => this.onVendorListChangedHandlers.delete(handler);
  }

  /** Registers an extra JobChatMessage listener next to the chat pop-up's own (see
   *  {@link onJobChatMessageHandlers}). Multi-subscriber; returns an unsubscribe function. */
  setJobChatMessageListener(handler: (dto: JobChatMessageDto) => void): () => void {
    this.onJobChatMessageHandlers.add(handler);
    return () => this.onJobChatMessageHandlers.delete(handler);
  }

  /** Registers a listener for OnsiteApprovalChanged (an on-site approval moved, incl. vendor send-back)
   *  on the staff inbox connection. Multi-subscriber; returns an unsubscribe function. */
  setOnsiteApprovalChangedListener(handler: (dto: OnsiteApprovalChangedDto) => void): () => void {
    this.onOnsiteApprovalChangedHandlers.add(handler);
    return () => this.onOnsiteApprovalChangedHandlers.delete(handler);
  }

  /** Registers a listener for VendorEstimateChanged (a normal estimate's negotiation moved — the Estimates
   *  tab's own live signal). Multi-subscriber; returns an unsubscribe function. */
  setVendorEstimateChangedListener(handler: (dto: VendorEstimateChangedDto) => void): () => void {
    this.onVendorEstimateChangedHandlers.add(handler);
    return () => this.onVendorEstimateChangedHandlers.delete(handler);
  }

  /** Registers a listener for OnsiteViewing (another admin opened/closed an on-site approval). Multi-subscriber. */
  setOnsiteViewingListener(handler: (dto: OnsiteViewingDto) => void): () => void {
    this.onOnsiteViewingHandlers.add(handler);
    return () => this.onOnsiteViewingHandlers.delete(handler);
  }

  /**
   * Tell the other admins this pop-up has an on-site approval open (or closed it). Best-effort: older Job Ops
   * without the hub method, or a dropped connection, simply means nobody sees "also open by".
   */
  async notifyOnsiteViewing(jobKey: string, vendorKey: string, viewing: boolean): Promise<void> {
    const h = this.hub;
    if (!h || h.state !== HubConnectionState.Connected || !jobKey || !vendorKey) return;
    try {
      await h.invoke('NotifyOnsiteViewing', jobKey, vendorKey, viewing);
    } catch {
      /* best-effort */
    }
  }

  /**
   * Ensure the shared staff-inbox hub is connected so broadcast listeners (e.g. VendorListChanged,
   * the accounting list events) actually fire — WITHOUT changing the chat message handlers. Pages
   * that need live list updates but don't own the inbox chat (the Vendor List) must call this:
   * landing on such a route directly otherwise leaves the hub unconnected, so its broadcasts never
   * arrive. Preserves any handlers the chat widget already registered.
   */
  async ensureBroadcastsConnected(): Promise<void> {
    if (this.hub?.state === HubConnectionState.Connected && this.inboxJoined) return;
    await this.ensureInboxConnected(this.onMessage ?? (() => {}), this.onThreadRead);
  }

  /** Called after the staff inbox connection comes back from a drop. Returns an unsubscribe function. */
  setInboxReconnectedListener(handler: () => void): () => void {
    this.onInboxReconnectedHandlers.add(handler);
    return () => this.onInboxReconnectedHandlers.delete(handler);
  }

  /** Persistent staff inbox subscription (badge + live thread/list updates). */
  async ensureInboxConnected(
    onMessage: (dto: JobChatMessageDto) => void,
    onThreadRead?: (dto: JobChatThreadReadEventDto) => void,
  ): Promise<void> {
    this.onMessage = onMessage;
    this.onThreadRead = onThreadRead;
    this.wantInbox = true;
    this.wireLifecycle();
    if (this.hub?.state === HubConnectionState.Connected && this.inboxJoined) {
      return;
    }
    // One connection at a time (the pop-up asks on start, on open and when it opens a thread).
    if (this.connecting) return this.connecting;
    // During an outage the back-off owns the timing: a component that initialises now must not start an extra
    // attempt (or restart the quick retries). Hidden/offline/refused/expired resume through their own events.
    if (this.retryTimer) return;
    if (this.paused === 'hidden' && !this.isTabVisible()) return;
    if (this.paused === 'offline' && !this.isOnline()) return;
    if (this.paused === 'refused' || this.paused === 'session-expired') return;
    if (this.paused === 'no-token' && !this.auth.getToken()) return;
    return this.attemptNow();
  }

  /** Starts one connection attempt now (the only caller of {@link connectInbox}). */
  private attemptNow(): Promise<void> {
    if (this.connecting) return this.connecting;
    this.clearRetryTimer();
    this.paused = null;
    this.connecting = this.connectInbox().finally(() => {
      this.connecting = undefined;
    });
    return this.connecting;
  }

  /** Builds the hub. Isolated so specs can hand in a fake connection. */
  protected buildHub(url: string, accessTokenFactory: () => string): HubConnection {
    return new HubConnectionBuilder()
      .withUrl(url, {
        // Read on every attempt (negotiate and the WebSocket), so a token picked up from another tab is used.
        accessTokenFactory,
        skipNegotiation: false,
        transport: HttpTransportType.WebSockets | HttpTransportType.ServerSentEvents,
        // Remembers the negotiate status so a 401 stops the retries (SignalR keeps only the error text).
        httpClient: new StatusTrackingHttpClient(this.createHttpClient(this.hubLogger), (status) => {
          this.lastHttpStatus = status;
        }),
        logger: this.hubLogger,
      })
      // Warnings and real errors only, redacted: no "WebSocket connected to wss://...access_token=..." lines.
      .configureLogging(this.hubLogger)
      .withServerTimeout(LIVE_SERVER_TIMEOUT_MS)
      .withKeepAliveInterval(LIVE_KEEP_ALIVE_MS)
      // No withAutomaticReconnect: the single retry loop below decides (back-off, stop on 401, pause when hidden).
      .build();
  }

  /** SignalR's own HTTP client (fetch). Isolated so specs can run the real hub stack against a fake server. */
  protected createHttpClient(logger: ILogger): HttpClient {
    return new DefaultHttpClient(logger);
  }

  private async connectInbox(): Promise<void> {
    this.clearRetryTimer();
    await this.teardown();
    if (!this.wantInbox) return;

    // No token at all (never signed in on this tab): stay quiet, as before; a token from another tab starts it.
    if (!this.auth.getToken()) {
      this.paused = 'no-token';
      return;
    }
    // Never start with an expired token: that is the endless-401 loop. Stop and show the banner instead.
    if (!this.auth.hasLiveToken()) {
      this.stopForSession();
      return;
    }

    const url = `${environment.apiBaseUrl}/hubs/job-chat`;
    this.lastHttpStatus = null;
    this.hub = this.buildHub(url, () => this.auth.getToken());

    // Hub callbacks run outside Angular; re-enter the zone so open-thread UI (seen ticks) refreshes.
    this.hub.on('JobChatMessage', (raw: JobChatMessageDto) => {
      const dto = normalizeJobChatMessageDto(raw);
      if (!dto) return;
      this.ngZone.run(() => {
        this.onMessage?.(dto);
        this.onJobChatMessageHandlers.forEach((h) => h(dto));
      });
    });

    this.hub.on('JobChatThreadRead', (raw: JobChatThreadReadEventDto) => {
      const dto = parseJobChatThreadReadEvent(raw);
      if (!dto) return;
      this.ngZone.run(() => this.onThreadRead?.(dto));
    });

    this.hub.on('DepositApprovalDecided', (dto: DepositApprovalDecidedDto) => {
      this.ngZone.run(() => this.onDepositApprovalDecided?.(dto));
    });

    this.hub.on('AccountingNoteChanged', (dto: AccountingNoteChangedDto) => {
      this.ngZone.run(() => this.onAccountingNoteChanged?.(dto));
    });

    this.hub.on('JobNotesActivityChanged', (dto: JobNotesActivityChangedDto) => {
      this.ngZone.run(() => this.onJobNotesActivityChanged?.(dto));
    });

    this.hub.on('ChangeRequestChanged', (dto: ChangeRequestChangedDto) => {
      this.ngZone.run(() => this.onChangeRequestChanged?.(dto));
    });

    this.hub.on('SendBackToServiceChanged', (dto: SendBackToServiceChangedDto) => {
      this.ngZone.run(() => this.onSendBackToServiceChangedHandlers.forEach((h) => h(dto)));
    });

    this.hub.on('JobRoutingChanged', (dto: JobRoutingChangedDto) => {
      this.ngZone.run(() => this.onJobRoutingChanged?.(dto));
    });

    this.hub.on('StoreManagerVerificationChanged', (dto: StoreManagerVerificationChangedDto) => {
      this.ngZone.run(() => this.onStoreManagerVerificationChanged?.(dto));
    });

    this.hub.on('VendorPayableChanged', (dto: VendorPayableChangedDto) => {
      this.ngZone.run(() => this.onVendorPayableChangedHandlers.forEach((h) => h(dto)));
    });

    this.hub.on('InvoiceCustomerCardChanged', (dto: InvoiceCustomerCardChangedDto) => {
      this.ngZone.run(() => this.onInvoiceCustomerCardChangedHandlers.forEach((h) => h(dto)));
    });

    for (const evt of ['InvoiceCustomerListChanged', 'AccountingBillsListChanged', 'AccountingPayablesReceivablesListChanged']) {
      this.hub.on(evt, () => {
        this.ngZone.run(() => this.onAccountingListsChangedHandlers.forEach((h) => h()));
      });
    }

    this.hub.on('OnsiteApprovalChanged', (raw: unknown) => {
      const dto = parseOnsiteApprovalChanged(raw);
      if (!dto) return;
      this.ngZone.run(() => this.onOnsiteApprovalChangedHandlers.forEach((h) => h(dto)));
    });
    this.hub.on('VendorEstimateChanged', (raw: unknown) => {
      const dto = parseVendorEstimateChanged(raw);
      if (!dto) return;
      this.ngZone.run(() => this.onVendorEstimateChangedHandlers.forEach((h) => h(dto)));
    });
    this.hub.on('OnsiteViewing', (raw: unknown) => {
      const dto = parseOnsiteViewing(raw);
      if (!dto) return;
      this.ngZone.run(() => this.onOnsiteViewingHandlers.forEach((h) => h(dto)));
    });
    this.hub.on('VendorListChanged', () => {
      this.ngZone.run(() => this.onVendorListChangedHandlers.forEach((h) => h()));
    });

    const hub = this.hub;
    hub.onclose((error) => {
      // Closed for good (not by us replacing it): the retry loop decides what comes next.
      if (this.hub !== hub) return;
      this.inboxJoined = false;
      this.handleFailure(error, false);
    });

    try {
      await hub.start();
      await hub.invoke('JoinStaffInbox');
    } catch (e) {
      // Job Ops unreachable right now (restarting, offline) or the session is gone: the policy decides.
      if (this.hub === hub) this.handleFailure(e, true);
      return;
    }
    if (this.hub !== hub) return;
    this.inboxJoined = true;
    this.failedAttempts = 0;
    this.paused = null;
    this.clearRetryTimer();
    this.scheduleExpiryStop();
    if (this.outageLogged) {
      this.outageLogged = false;
      console.info('[live] Live chat reconnected');
    }
    const recovered = this.everConnected;
    this.everConnected = true;
    if (recovered) this.notifyInboxReconnected();
  }

  private notifyInboxReconnected(): void {
    this.ngZone.run(() => this.onInboxReconnectedHandlers.forEach((h) => h()));
  }

  /**
   * After a drop (`attemptFailed` false) or a failed attempt (true): retry with back-off, pause, or stop.
   * This is the single retry loop; it never logs the error itself (it can contain the hub URL).
   */
  private handleFailure(error: unknown, attemptFailed: boolean): void {
    if (!this.wantInbox) return;
    if (attemptFailed) this.failedAttempts++;
    const status = statusFromError(error, this.lastHttpStatus);
    this.lastHttpStatus = null;
    const decision = decideRetry({
      failedAttempts: this.failedAttempts,
      status,
      tokenLive: this.auth.hasLiveToken(),
      visible: this.isTabVisible(),
      online: this.isOnline(),
      random: () => this.random(),
    });
    switch (decision.kind) {
      case 'session-expired':
        void this.teardown();
        this.stopForSession();
        return;
      case 'refused':
        this.clearRetryTimer();
        this.paused = 'refused';
        console.warn(`[live] Live chat stopped: the server refused the connection (HTTP ${decision.status})`);
        return;
      case 'offline':
      case 'hidden':
        this.clearRetryTimer();
        this.paused = decision.kind;
        this.logOutageStart();
        return;
      case 'retry':
        this.logOutageStart();
        this.scheduleRetry(decision.delayMs);
        return;
    }
  }

  private logOutageStart(): void {
    if (this.outageLogged) return;
    this.outageLogged = true;
    console.warn('[live] Live chat disconnected, retrying');
  }

  /** Replaces any pending retry: there is never more than one retry timer. */
  private scheduleRetry(delayMs: number): void {
    this.clearRetryTimer();
    this.retryTimer = setTimeout(() => {
      this.retryTimer = undefined;
      if (!this.wantInbox) return;
      if (this.hub?.state === HubConnectionState.Connected && this.inboxJoined) return;
      // The tab was hidden or went offline while waiting: pause; visibilitychange / online resumes.
      if (!this.isOnline()) {
        this.paused = 'offline';
        return;
      }
      if (!this.isTabVisible()) {
        this.paused = 'hidden';
        return;
      }
      void this.attemptNow();
    }, delayMs);
  }

  private clearRetryTimer(): void {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = undefined;
    }
  }

  /** Session over (expired token or hub 401): no more attempts until a fresh token appears; show the banner. */
  private stopForSession(): void {
    this.clearRetryTimer();
    this.clearExpiryTimer();
    const already = this.paused === 'session-expired';
    this.paused = 'session-expired';
    this.outageLogged = false;
    if (!already) console.warn('[live] Live chat stopped: your session has expired');
    this.sessionNotice.markExpired();
  }

  /** Live updates stop exactly at the token's `exp` (the server keeps an open socket past it). */
  private scheduleExpiryStop(): void {
    this.clearExpiryTimer();
    const secs = this.auth.secondsUntilExpiry();
    if (secs <= 0) return;
    // setTimeout caps at ~24.8 days; an admin token lasts 4 h, so a re-check on overflow is enough.
    const ms = Math.min(secs * 1000, 2_147_000_000);
    this.expiryTimer = setTimeout(() => {
      this.expiryTimer = undefined;
      if (!this.wantInbox) return;
      if (this.auth.hasLiveToken()) {
        this.scheduleExpiryStop();
        return;
      }
      void this.teardown();
      this.stopForSession();
    }, ms);
  }

  private clearExpiryTimer(): void {
    if (this.expiryTimer) {
      clearTimeout(this.expiryTimer);
      this.expiryTimer = undefined;
    }
  }

  /**
   * Back online, back to the tab, or a fresh token: one attempt now, then the normal back-off. A session stop is
   * only lifted by a live token; the back-off count is kept so tab switching does not restart the quick retries.
   */
  private resume(reason: 'online' | 'visible' | 'token'): void {
    if (!this.wantInbox) return;
    if (this.hub?.state === HubConnectionState.Connected && this.inboxJoined) {
      // Healthy connection: only move the expiry stop to the new token's exp.
      if (reason === 'token') this.scheduleExpiryStop();
      return;
    }
    if (this.connecting) return;
    if (!this.auth.getToken()) {
      // Never signed in on this tab: stay quiet (no banner) until a token appears.
      this.clearRetryTimer();
      this.paused = 'no-token';
      return;
    }
    if (!this.auth.hasLiveToken()) {
      this.stopForSession();
      return;
    }
    if (this.paused === 'session-expired' || this.paused === 'no-token') {
      if (reason !== 'token') return;
      this.failedAttempts = 0;
      this.sessionNotice.clear();
    }
    // A visible, online tab already waiting on its back-off timer keeps it (no extra attempt per tab switch).
    if (reason === 'visible' && !this.paused && this.retryTimer) return;
    void this.attemptNow();
  }

  /** Coming back online or to the tab, or a fresh token from another tab: reconnect now. */
  private wireLifecycle(): void {
    if (this.lifecycleWired || typeof window === 'undefined') return;
    this.lifecycleWired = true;
    window.addEventListener('online', this.onOnline);
    document.addEventListener('visibilitychange', this.onVisibility);
    this.tokenSub = this.auth.tokenChanges?.subscribe(() => this.resume('token'));
  }

  private unwireLifecycle(): void {
    if (!this.lifecycleWired) return;
    this.lifecycleWired = false;
    window.removeEventListener('online', this.onOnline);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.tokenSub?.unsubscribe();
    this.tokenSub = undefined;
  }

  /** Seams for specs. */
  protected isTabVisible(): boolean {
    return typeof document === 'undefined' || document.visibilityState !== 'hidden';
  }

  protected isOnline(): boolean {
    return typeof navigator === 'undefined' || navigator.onLine !== false;
  }

  protected random(): number {
    return Math.random();
  }

  /** @deprecated Use {@link ensureInboxConnected}; kept for call sites during thread load. */
  async ensureConnected(
    _jobKey: string,
    _threadVendorKey: string | null,
    onMessage: (dto: JobChatMessageDto) => void,
    onThreadRead?: (dto: JobChatThreadReadEventDto) => void,
  ): Promise<void> {
    await this.ensureInboxConnected(onMessage, onThreadRead);
  }

  /** Stops the staff inbox for good: no reconnect, every timer and listener removed. */
  async stop(): Promise<void> {
    this.wantInbox = false;
    this.clearRetryTimer();
    this.clearExpiryTimer();
    this.paused = null;
    this.failedAttempts = 0;
    this.outageLogged = false;
    this.unwireLifecycle();
    await this.teardown();
  }

  /** @deprecated Same as {@link stop}. */
  disconnect(): Promise<void> {
    return this.stop();
  }

  ngOnDestroy(): void {
    void this.stop();
  }

  /** Drops the current connection (before a new one is built). */
  private async teardown(): Promise<void> {
    const h = this.hub;
    this.inboxJoined = false;
    this.hub = undefined;

    if (h && h.state === HubConnectionState.Connected) {
      try {
        await h.invoke('LeaveStaffInbox');
      } catch {
        /* leave best-effort */
      }
    }
    if (h) {
      try {
        await h.stop();
      } catch {
        /* stop best-effort */
      }
    }
  }
}
